import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
var _class, _dec, _dec2, _class2, _descriptor, _descriptor2, _descriptor3, _ErrorDialogMachine;
import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { ClientErrorRendererMachine } from "abreact/incubator/ui/error/ClientErrorRendererMachine";
import { FailedToFetchErrorRendererMachine } from "abreact/incubator/ui/error/FailedToFetchErrorRendererMachine";
import { RemoteErrorRendererMachine } from "abreact/incubator/ui/error/RemoteErrorRendererMachine";
import { StandardErrorRendererMachine } from "abreact/incubator/ui/error/StandardErrorRendererMachine";
import { BaseMachine } from "abreact/incubator/ui/machine/BaseMachine";
import { createReactionName } from "abreact/incubator/ui/utils/MobXUtils";
import { RemoteAbInitioErrorException } from "abweb/dto/exception/RemoteAbInitioErrorException";
import { RemoteError } from "abweb/dto/exception/RemoteError";
import { FailedToFetchError } from "abweb/service/utils/rpc/FailedToFetchError";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { DateTimeUtils } from "abweb/utils/date/DateTimeUtils";
import { ClientError, ErrorExtra, ErrorUtils } from "abweb/utils/ErrorUtils";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { SourceMapUtils } from "abweb/utils/SourceMapUtils";
import { action, computed, observable, reaction, makeObservable } from "mobx";
export let ErrorInfo = (_class = class ErrorInfo {
  constructor(error, timestamp) {
    _defineProperty(this, "_error", void 0);
    _defineProperty(this, "_timestamp", void 0);
    this._error = error;
    if (!timestamp) {
      timestamp = new Date();
    }
    this._timestamp = timestamp;
    makeObservable(this);
  }
  get url() {
    return ErrorUtils.getErrorExtra(this._error, ErrorExtra.rpcServiceErrorUrl);
  }
  get rpcCallStack() {
    return ErrorUtils.getErrorExtra(this._error, ErrorExtra.rpcServiceErrorCallStack);
  }
  get error() {
    return this._error;
  }
  get timestamp() {
    return this._timestamp;
  }
  get formattedTimestamp() {
    return this.timestamp ? DateTimeUtils.FORMAT_TIMESTAMP_MSEC.format(this.timestamp) : "";
  }
}, _applyDecoratedDescriptor(_class.prototype, "formattedTimestamp", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "formattedTimestamp"), _class.prototype), _class);
export let ErrorDialogMachine = (_dec = observable.shallow, _dec2 = action.bound, _class2 = (_ErrorDialogMachine = class ErrorDialogMachine extends BaseMachine {
  constructor(error, timestamp, description) {
    super();
    _initializerDefineProperty(this, "_errors", _descriptor, this);
    _initializerDefineProperty(this, "_index", _descriptor2, this);
    _initializerDefineProperty(this, "_errorMachine", _descriptor3, this);
    _defineProperty(this, "_description", void 0);
    makeObservable(this);
    this._errorMachine = undefined;
    if (ClassUtils.isInstanceOf(error, Array)) {
      this._errors = error.map(err => new ErrorInfo(err, timestamp));
    } else {
      this._errors = [new ErrorInfo(error, timestamp)];
    }
    this._index = 0;
    this._description = description;
    this.registerDestroyListener(reaction(() => this.currentError, this.onCurrentErrorChanged, createReactionName("ErrorDialogMachine.currentError", true)));
  }
  get description() {
    return this._description;
  }
  get canShowSupportInformation() {
    return this.url !== undefined || this.currentErrorMachine.stackTrace !== null;
  }
  get url() {
    return this._errors[this._index].url;
  }
  get complete() {
    return this._index === -1;
  }
  get currentError() {
    return this._errors[this._index];
  }
  get currentErrorMachine() {
    return this._errorMachine;
  }
  get errorIndex() {
    return this._index;
  }
  get errorCount() {
    return this._errors.length;
  }
  get hasNextError() {
    return this._index < this._errors.length - 1;
  }
  get hasPreviousError() {
    return this._index > 0;
  }
  onCurrentErrorChanged(currentError) {
    this._errorMachine = this.getErrorRendererMachine(currentError);
  }
  destroy() {
    this.debug("destroying");
    this._errors = [];
    this._index = -1;
    return super.destroy();
  }
  addError(error) {
    this.debug("Adding error: " + error);
    this._errors.push(new ErrorInfo(error));
  }
  gotoNextError() {
    if (this.hasNextError) {
      this._index++;
    }
  }
  gotoPreviousError() {
    if (this.hasPreviousError) {
      this._index--;
    }
  }
  async getSupportInformation() {
    return this.canShowSupportInformation ? await this.getClipboardText() : undefined;
  }
  async getClipboardText() {
    let text = this.url ? `${AbReactResources.error_rpcUrl()} ${this.url}\n\n` : "";
    const errorTexts = await Promise.all(this._errors.map(async (errorInfo, index) => {
      let errorText = "";
      const renderer = this.getErrorRendererMachine(errorInfo);
      const errorStackText = await this.getClipboardTextForErrorRendererMachine(renderer, errorInfo);
      if (index > 0) {
        errorText += "\n" + AbReactResources.error_clipboard_divider() + "\n";
      }
      errorText += `${AbReactResources.error_clipboard_number()}: ${index}`;
      return errorText + "\n" + errorStackText;
    }));
    return [text, ...errorTexts].join("");
  }
  makeLine(s, level) {
    const prefix = level > 0 ? "==> " : "";
    return s.split("\n").map(line => `${prefix}${line}`).join("\n");
  }
  async getClipboardTextForErrorRendererMachine(errorRendererMachine, errorInfo, level = 0, initialText = "") {
    let text = initialText;
    if (errorInfo) {
      text = this.makeLine(`${text}${AbReactResources.error_dateTime()} ${errorInfo.formattedTimestamp} ${errorRendererMachine.message}`, level);
    } else {
      text = this.makeLine(`${text}${errorRendererMachine.message}`, level);
    }
    const detailText = errorRendererMachine.getSupportDetailsText();
    if (detailText) {
      text += "\n" + this.makeLine("\n" + detailText, level);
    }
    if (errorRendererMachine.stackTrace) {
      const cleanedTrace = await SourceMapUtils.cleanErrorStack({
        stack: errorRendererMachine.stackTrace
      });
      text += "\n" + this.makeLine("\n" + cleanedTrace, level);
    }
    if (errorRendererMachine.causedBy) {
      text += `\n\n${this.makeLine(AbReactResources.error_causedBy(), level)}`;
      const causedBy = errorRendererMachine.causedBy;
      if (causedBy) {
        if (ClassUtils.isInstanceOf(causedBy, Error)) {
          const renderer = this.getErrorRendererMachine(new ErrorInfo(errorRendererMachine.causedBy));
          const causedByClipboardText = await this.getClipboardTextForErrorRendererMachine(renderer, undefined, level + 1);
          text += "\n" + this.makeLine(causedByClipboardText, level);
        } else {
          const causedByTexts = await Promise.all(causedBy.map(async (error, index) => {
            let causedByText = "\n";
            const renderer = this.getErrorRendererMachine(new ErrorInfo(error));
            const causedByClipboardText = await this.getClipboardTextForErrorRendererMachine(renderer, undefined, level + 1, causedBy.length > 1 ? `${index}: ` : "");
            causedByText += this.makeLine((index > 0 ? "\n" : "") + causedByClipboardText, level);
            return causedByText;
          }));
          text += causedByTexts.join("");
        }
      }
    }
    if (errorInfo && errorInfo.rpcCallStack) {
      const cleanedRpcCallStack = await SourceMapUtils.cleanErrorStack({
        stack: errorInfo.rpcCallStack
      });
      text += `\n\n${this.makeLine(AbReactResources.error_clientCallStack(), level)}`;
      text += "\n" + this.makeLine(cleanedRpcCallStack, level);
    }
    return text;
  }
  getErrorRendererMachine(errorInfo) {
    if (!errorInfo) {
      return {
        title: "",
        message: "",
        causedBy: null,
        showCausedBy: false,
        stackTrace: null,
        getSupportDetailsText: () => null
      };
    } else if (ClassUtils.isInstanceOf(errorInfo.error, FailedToFetchError)) {
      return new FailedToFetchErrorRendererMachine(errorInfo.error);
    } else if (ClassUtils.isInstanceOf(errorInfo.error, ClientError)) {
      return new ClientErrorRendererMachine(errorInfo.error);
    } else if (ClassUtils.isInstanceOf(errorInfo.error, RemoteAbInitioErrorException)) {
      return new RemoteErrorRendererMachine(new RemoteError(errorInfo.error.message, null, null, errorInfo.error), errorInfo.error.exceptionTime ?? new Date());
    } else if (ClassUtils.isInstanceOf(errorInfo.error, RemoteError)) {
      return new RemoteErrorRendererMachine(errorInfo.error, errorInfo.timestamp);
    } else {
      return new StandardErrorRendererMachine(errorInfo.error);
    }
  }
  debug(s) {
    ErrorDialogMachine.LOGGER.debug(s);
  }
}, _defineProperty(_ErrorDialogMachine, "LOGGER", LogUtils.getLogger(__filename)), _ErrorDialogMachine), _descriptor = _applyDecoratedDescriptor(_class2.prototype, "_errors", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class2.prototype, "_index", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class2.prototype, "_errorMachine", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class2.prototype, "canShowSupportInformation", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "canShowSupportInformation"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "url", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "url"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "complete", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "complete"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "currentError", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "currentError"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "currentErrorMachine", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "currentErrorMachine"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "errorIndex", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "errorIndex"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "errorCount", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "errorCount"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "hasNextError", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "hasNextError"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "hasPreviousError", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "hasPreviousError"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "onCurrentErrorChanged", [_dec2], Object.getOwnPropertyDescriptor(_class2.prototype, "onCurrentErrorChanged"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "addError", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "addError"), _class2.prototype), _class2);
//# sourceMappingURL=ErrorDialogMachine.js.map
